// handDetector — MediaPipe HandLandmarker wrapper for the
// `mediapipe.hand_tracking` sub-feature.
//
// Two detection patterns are folded into one state machine, both triggered
// by the same flag (`mediapipe.hand_tracking`):
//
//   A. Hands off-screen for sustained time. Classic "phone under the desk"
//      pattern — both hands disappear from the webcam view for >5 seconds
//      while the candidate's face stays visible (they're reading a phone
//      hidden below the laptop). Threshold key: `hands_off_seconds`.
//
//   B. Extra hand detected. A 3rd visible hand means a second person is in
//      frame, even if their face is out of shot. Far stronger signal than
//      a fleeting glimpse of multiple faces. Threshold key:
//      `extra_hand_threshold` (default 3).
//
// Why a separate detector (not folded into faceDetector):
//   * Different MediaPipe model (HandLandmarker, not FaceLandmarker), so
//     we'd have to instantiate the model anyway.
//   * Cleaner separation of concerns — adding/disabling hand detection
//     doesn't touch face logic.
//
// Public API matches faceDetector: { start, stop, isReady }.

import { loadVisionTasks, MODEL_URLS } from '../mediapipeLoader';
import { createDurationStateMachine } from '../stateMachine';

const FEATURE_HAND_TRACKING = 'mediapipe.hand_tracking';

// 5fps is plenty for the multi-second behaviours we care about, and
// matches the face detector's cadence so the two share CPU evenly.
const DEFAULT_FPS = 5;

// Maximum hands we ask MediaPipe to track. Setting this to 4 keeps the
// model fast while still letting us count "3+ hands = another person".
const MAX_HANDS_TO_DETECT = 4;

const num = (obj, key, fallback) => {
  const v = obj?.[key];
  if (v === null || v === undefined) return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

export function createHandDetector({
  videoElement,
  emit,
  getFlag,
  getThreshold,
  fps = DEFAULT_FPS,
}) {
  if (!videoElement) {
    throw new Error('[handDetector] videoElement is required');
  }
  if (typeof emit !== 'function') {
    throw new Error('[handDetector] emit must be a function');
  }
  if (typeof getFlag !== 'function' || typeof getThreshold !== 'function') {
    throw new Error('[handDetector] getFlag and getThreshold must be functions');
  }

  let landmarker = null;
  let running = false;
  let intervalId = null;
  let ready = false;

  // Two state machines because the two patterns have different durations
  // and severities even though they share a feature key.
  let smHandsOff = null;
  let smExtraHand = null;

  const ensureLandmarker = async () => {
    if (landmarker) return landmarker;
    const { vision, modules } = await loadVisionTasks();
    const { HandLandmarker } = modules;

    landmarker = await HandLandmarker.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: MODEL_URLS.handLandmarker,
        delegate: 'GPU',
      },
      runningMode: 'VIDEO',
      numHands: MAX_HANDS_TO_DETECT,
    });
    ready = true;
    return landmarker;
  };

  const initStateMachines = () => {
    const thr = getThreshold(FEATURE_HAND_TRACKING);
    const handsOffSeconds = num(thr, 'hands_off_seconds', 5);
    const extraHandThreshold = num(thr, 'extra_hand_threshold', 3);

    // Hands-off pattern: warn at threshold, escalate after +5s.
    smHandsOff = createDurationStateMachine({
      warnAfterMs: handsOffSeconds * 1000,
      flagAfterMs: (handsOffSeconds + 5) * 1000,
      onWarn: () => {
        emit({
          feature_key: FEATURE_HAND_TRACKING,
          severity: 'low',
          score_delta: 2,
          message: 'Hands not visible — please keep your hands on the keyboard',
          evidence: { stage: 'warn', pattern: 'hands_off_screen' },
        });
      },
      onFlag: () => {
        emit({
          feature_key: FEATURE_HAND_TRACKING,
          severity: 'high',
          score_delta: 8,
          message: 'Hands not visible for extended time — possible phone use',
          evidence: { stage: 'flag', pattern: 'hands_off_screen' },
        });
      },
    });

    // Extra-hand pattern: critical from the start (second person is a
    // very strong signal — no benign explanation).
    smExtraHand = createDurationStateMachine({
      // Need 1+ seconds to filter out transient false positives where
      // MediaPipe momentarily detects fingers as an extra hand.
      warnAfterMs: 1000,
      flagAfterMs: 1000,
      onWarn: () => {
        emit({
          feature_key: FEATURE_HAND_TRACKING,
          severity: 'critical',
          score_delta: 20,
          message: 'Extra hand detected in frame — another person may be present',
          evidence: {
            stage: 'flag',
            pattern: 'extra_hand',
            extra_hand_threshold: extraHandThreshold,
          },
        });
      },
      onFlag: () => {},
    });
  };

  const tick = () => {
    if (!running || !landmarker || !videoElement) return;
    if (videoElement.readyState < 2) return;

    let result;
    try {
      result = landmarker.detectForVideo(videoElement, performance.now());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[handDetector] detectForVideo failed:', err?.message || err);
      return;
    }

    if (!getFlag(FEATURE_HAND_TRACKING)) {
      if (smHandsOff) smHandsOff.reset();
      if (smExtraHand) smExtraHand.reset();
      return;
    }

    const thr = getThreshold(FEATURE_HAND_TRACKING);
    const extraHandThreshold = num(thr, 'extra_hand_threshold', 3);

    const handCount = result?.landmarks?.length || 0;

    // Pattern A: no hands visible at all.
    smHandsOff.update(handCount === 0);

    // Pattern B: 3+ hands visible (assuming candidate has 2 → 3rd is another
    // person). We treat the configured `extra_hand_threshold` as the count
    // at which "extra" begins.
    smExtraHand.update(handCount >= extraHandThreshold);
  };

  const start = async () => {
    if (running) return;
    await ensureLandmarker();
    initStateMachines();
    running = true;
    intervalId = setInterval(tick, Math.round(1000 / fps));
  };

  const stop = () => {
    running = false;
    if (intervalId) {
      clearInterval(intervalId);
      intervalId = null;
    }
    if (smHandsOff) smHandsOff.reset();
    if (smExtraHand) smExtraHand.reset();
    if (landmarker) {
      try { landmarker.close(); } catch (_) { /* already disposed */ }
      landmarker = null;
    }
    ready = false;
  };

  const isReady = () => ready;

  return { start, stop, isReady };
}
